/**
 * TruSpace Privacy Notice — Version 1.5, effective August 22, 2026.
 *
 * Supersedes the Privacy Notice last updated April 2023. When legal publishes a
 * new version, insert a new `agreements` row titled "Privacy Notice" with a
 * newer `create_date` and update this component in the same change — never edit
 * an existing row. The forced-acceptance gate treats the latest row per title as
 * current (`server/src/features/auth/auth.legal.ts`).
 */

import {
  LegalDocumentHeader,
  LegalExternalLink,
  LegalInShort,
  LegalList,
  LegalMailto,
  LegalSection,
  LegalSubHeading,
  LegalTable,
} from '@/features/auth/components/LegalProse';

/** CCPA/CPRA statutory categories mapped to what TruSpace actually collects. */
const CALIFORNIA_CATEGORY_ROWS: string[][] = [
  [
    'A — Identifiers',
    'Name, business email, business phone, username, IP address, account identifier',
    'Yes',
  ],
  [
    'B — Cal. Civ. Code §1798.80 records',
    'Name, employer, job title, business contact details',
    'Yes',
  ],
  ['C — Protected classifications', 'Race, religion, age, sex, disability and similar', 'No'],
  [
    'D — Commercial information',
    'Subscription level, authorization records, invoice and payment history',
    'Yes',
  ],
  ['E — Biometric information', 'Fingerprints, faceprints, voiceprints', 'No'],
  [
    'F — Internet or network activity',
    'Log data, pages viewed, features used, support interactions, and session recordings and error reports used for bug repair',
    'Yes',
  ],
  ['G — Geolocation data', 'Approximate area inferred from IP address only', 'Yes — approximate only'],
  [
    'H — Audio, visual, or similar',
    'Photographs of buildings and building interiors taken during field verification. These depict property, not people, though individuals may be incidentally captured',
    'Limited',
  ],
  [
    'I — Professional or employment information',
    'Job title, employer, role within the client organization',
    'Yes',
  ],
  ['J — Education information', 'Records covered by FERPA', 'No'],
  ['K — Inferences', 'Profiles reflecting preferences, characteristics, or behavior', 'No'],
  [
    'L — Sensitive personal information',
    'Account login credentials (username combined with password). Used solely to authenticate you and maintain account security',
    'Yes — credentials only',
  ],
];

/** Sub-processors. The published list lives at truspace.com/subprocessors. */
const SERVICE_PROVIDER_ROWS: string[][] = [
  ['Supabase', 'Application database and sign-in', 'United States (North America)'],
  ['Cloudflare R2', 'Drawings and generated reports', 'Western North America'],
  [
    'Microsoft Azure',
    'File storage, Dynamics 365 / Dataverse, and Microsoft Entra ID',
    'United States (US West)',
  ],
  ['Railway', 'Application servers', 'United States (San Francisco)'],
  ['Vercel', 'Website and application front end', 'United States'],
  ['Cloudflare', 'Space-calculation (CAD) service', 'Cloudflare network (in transit)'],
  ['Anthropic', 'TruInsight AI answers', 'Anthropic network (in transit)'],
  ['Microsoft Clarity', 'Session recordings used only to fix product issues', 'Microsoft'],
  ['Sentry', 'Error reports used only to fix bugs', 'United States (Northwest)'],
  ['n8n Cloud', 'Support and feedback automation', 'Germany'],
  ['Zendesk', 'Support tickets and in-product chat', 'Zendesk network'],
  [
    'Google Maps',
    'Address lookup and maps — requested directly by your browser',
    'Google network',
  ],
  [
    'CARTO / OpenStreetMap',
    'Base map tiles — requested directly by your browser',
    'CARTO network',
  ],
];

/** Full Privacy Notice body for the auth dialogs and the acceptance gate. */
export function PrivacyPolicyContent() {
  return (
    <div className="space-y-6 text-sm leading-relaxed text-muted-foreground">
      <LegalDocumentHeader
        title="TruSpace Privacy Notice"
        version="Version 1.5"
        effectiveDate="August 22, 2026"
        supersedes="Supersedes the Privacy Notice last updated April 2023"
      />

      <LegalSection id="privacy-about" heading="1. ABOUT THIS NOTICE">
        <p>
          Stevenson Systems, Inc. (“Stevenson Systems,” “SSI,” “we,” “us,” or “our”)
          provides building measurement, square footage calculation, and space accounting
          services, delivered through the TruSpace platform at truspace.com and
          app.truspace.com (together, the “Services”).
        </p>
        <p>
          This Privacy Notice explains how we handle personal information in connection
          with the Services and our website. It applies to visitors to our website,
          individuals who register for or use a TruSpace account, and people who contact
          us about our business.
        </p>
        <p>
          <span className="font-semibold text-foreground">
            What this Notice does not cover.
          </span>{' '}
          It does not change any agreement between Stevenson Systems and a client
          organization, and it grants us no rights over a client’s data. Where a Master
          Services Agreement, Authorization Agreement, or Data Processing Addendum governs
          our handling of a client’s data, those documents control — including MSA Section
          19.6, under which the Agreement controls any conflicting term in a referenced
          document. This Notice describes what we do; the MSA sets what we may do. This
          Notice also does not cover third-party websites we link to.
        </p>
      </LegalSection>

      <LegalSection id="privacy-roles" heading="2. OUR TWO ROLES">
        <p>
          TruSpace is a business-to-business platform. Most of the information in the
          platform is submitted by an organization about its buildings and tenants, not by
          an individual about themselves. That distinction determines your rights, so we
          state it plainly.
        </p>
        <p>
          <span className="font-semibold text-foreground">
            When we act for ourselves.
          </span>{' '}
          For our website, marketing, account administration, billing, and support, we
          decide why and how personal information is used. Under U.S. state privacy laws we
          are a “business”; under the GDPR we would be a “controller.” Sections 3 through 6
          and Sections 8 through 12 describe this role.
        </p>
        <p>
          <span className="font-semibold text-foreground">When we act for a client.</span>{' '}
          When a client organization uploads Client Property Data — floor plans, stacking
          plans, lease and occupancy records that may name individual tenants or occupants
          — we handle it on that client’s instructions and for that client’s purposes.
          Under U.S. state privacy laws we are a “service provider”; under the GDPR we
          would be a “processor.” We do not sell that data, do not use it to build our own
          products or train AI models, and do not use it for our own marketing. Section 7
          explains how to exercise rights over that data.
        </p>
      </LegalSection>

      <LegalSection id="privacy-collect" heading="3. PERSONAL INFORMATION WE COLLECT">
        <LegalSubHeading>Information you provide</LegalSubHeading>
        <LegalList>
          <li>
            <span className="font-semibold text-foreground">
              Account and identity information.
            </span>{' '}
            Name, business email address, business phone number, job title, employer,
            username, and password.
          </li>
          <li>
            <span className="font-semibold text-foreground">
              Business contact information.
            </span>{' '}
            Details you give us when you request a proposal, respond to an inquiry, or
            attend an event.
          </li>
          <li>
            <span className="font-semibold text-foreground">Support communications.</span>{' '}
            The content of support tickets, emails, and any files or screenshots you
            attach.
          </li>
          <li>
            <span className="font-semibold text-foreground">Transaction records.</span>{' '}
            Subscription level, authorization and work authorization records, invoices, and
            payment status. We do not collect or store payment card numbers.
          </li>
        </LegalList>

        <LegalSubHeading>Information collected automatically</LegalSubHeading>
        <LegalList>
          <li>
            <span className="font-semibold text-foreground">Log and usage data.</span> IP
            address, browser type and version, operating system, referring pages, pages
            viewed, features used, timestamps, and error data.
          </li>
          <li>
            <span className="font-semibold text-foreground">Device data.</span> Device
            type, screen size, and similar technical characteristics.
          </li>
          <li>
            <span className="font-semibold text-foreground">Approximate location.</span> A
            general geographic area inferred from IP address. We do not collect precise
            device geolocation. Building locations shown on the Portfolio Map come from
            property records supplied by client organizations, not from your device.
          </li>
          <li>
            <span className="font-semibold text-foreground">
              Cookies and similar technologies.
            </span>{' '}
            A small number of first-party cookies and browser-storage entries used for
            sign-in, session management, and interface preferences. Session tokens expire
            after 30 days. See Section 12.
          </li>
          <li>
            <span className="font-semibold text-foreground">
              Session recordings and error reports.
            </span>{' '}
            We record product sessions and capture error reports so we can reproduce and
            repair problems. Section 12 explains what is masked, when recording pauses, and
            how long recordings are kept.
          </li>
        </LegalList>

        <LegalSubHeading>Client Property Data</LegalSubHeading>
        <p>
          Client organizations and their Authorized Users upload building and tenant
          information — construction drawings, stacking plans, suite numbers, lease
          commencement and termination dates, rentable square footage, tenant names, and
          occupancy records. Where this information identifies an individual, we handle it
          as described in Sections 2 and 7.
        </p>

        <LegalSubHeading>California categories</LegalSubHeading>
        <p>
          The table below maps what we collect to the statutory categories in the
          California Consumer Privacy Act, as amended by the CPRA.
        </p>
        <LegalTable
          label="California statutory categories and whether TruSpace collects them"
          headers={['Statutory category', 'What this means at TruSpace', 'Collected']}
          rows={CALIFORNIA_CATEGORY_ROWS}
        />
        <p>
          We collect these from you directly, automatically as you use the Services, and
          from the client organization that authorized your account. We do not sell
          personal information and we do not share it for cross-context behavioral
          advertising. We use sensitive personal information only for the purposes
          permitted by Cal. Civ. Code §1798.121(a) — authenticating you and securing your
          account — and never to infer characteristics about you.
        </p>
      </LegalSection>

      <LegalSection id="privacy-use" heading="4. HOW WE USE PERSONAL INFORMATION">
        <LegalList>
          <li>
            To create and administer accounts, authenticate users, and maintain account
            security.
          </li>
          <li>
            To deliver the Services, including generating and making available Calculation
            Analysis Reports, Certified Measurement Reports, and Space Accounting Reports.
          </li>
          <li>
            To provide support, respond to inquiries, and investigate and resolve problems.
          </li>
          <li>
            To invoice, collect payment, and manage subscriptions and renewals.
          </li>
          <li>
            To send service and administrative messages, including notices of changes to
            this Notice or our terms.
          </li>
          <li>
            To monitor, maintain, and secure the performance and reliability of the
            Services, including fraud prevention, abuse detection, and diagnosing and
            repairing faults.
          </li>
          <li>
            To send marketing communications about our services to business contacts, where
            permitted. We use only contact details obtained through our own marketing,
            inquiries, and events for this purpose. We do not use a client organization’s
            Confidential Information, or contact details drawn from Client Property Data,
            to market to anyone. You can opt out at any time using the link in any
            marketing email.
          </li>
          <li>
            To create and use photographs of the buildings we measure, including in
            advertising, promotion, and marketing. MSA Section 10.6 grants us that right
            for the Properties listed on a client’s Authorization Agreement, except for
            defamatory or pornographic use. These images depict property rather than
            people, though individuals may be incidentally captured; tell us at{' '}
            <LegalMailto email="support@truspace.com" /> if you appear in one and would
            like it addressed.
          </li>
          <li>
            To comply with law, respond to lawful requests, and establish, exercise, or
            defend legal claims.
          </li>
        </LegalList>
        <p>
          Client Property Data is used only to operate the Services for the organization
          that supplied it. That is the drawings, stacking plans, suite and lease records,
          tenant names, and occupancy information a client uploads or gives us. We do not
          use it to improve or train products, and we do not build benchmarks, market
          studies, or comparisons from it. MSA Section 10.5 limits our license in that data
          to operating the TruSpace Platform on the client’s behalf, and MSA Section 8.2
          limits our use of a client’s Confidential Information to what is necessary to
          perform the Agreement.
        </p>
        <p>
          We do aggregate information we create ourselves. That means the measurement and
          calculation work we produce and deliver, which Stevenson Systems owns under MSA
          Section 10.1.2, and the internal working records we generate in producing it,
          which are our own information. MSA Section 8.2 restricts each party’s use of the
          other party’s Confidential Information, so what matters is whose information it
          is rather than what it is about. We use those aggregates within Stevenson Systems
          to run, support, secure, and improve the Services. They exclude tenant, lease,
          and occupancy information. We do not publish them or otherwise disclose them
          outside Stevenson Systems, and we do not attempt to re-identify them. If that
          ever changes we will update this Notice before it does, and no published figure
          will identify an organization, a building, a tenant, or an individual.
        </p>
        <p>
          Platform usage telemetry is treated as the client’s rather than ours, because it
          records their activity rather than our work and MSA Section 8.1 lists TruSpace
          usage statistics as Confidential Information. We use it to operate, monitor,
          secure, and repair the Services and for nothing else.
        </p>
        <p>
          We do not use personal information for automated decision-making that produces
          legal or similarly significant effects. We do not conduct prize draws or
          competitions, post user testimonials containing personal information, or offer
          user-to-user messaging — all of which appeared in the prior template notice and
          none of which describe TruSpace.
        </p>
      </LegalSection>

      <LegalSection id="privacy-ai" heading="5. ARTIFICIAL INTELLIGENCE">
        <LegalInShort>
          Yes. TruInsight uses a third-party AI model to answer questions about your
          properties. Your property records are sent to that provider to generate the
          answer, and they are not used to train any Stevenson Systems model.
        </LegalInShort>
        <p>
          <span className="font-semibold text-foreground">TruInsight.</span> TruInsight AI
          uses Anthropic. When a user asks a question, we look up the answer in our United
          States database and send Anthropic the question plus a limited preview of matching
          property records — up to 30 rows — so it can write a plain-language answer. Those
          rows can include building names, addresses, tenant names, suite numbers, rentable
          area, and lease dates.
        </p>
        <p>
          <span className="font-semibold text-foreground">What we do not do.</span> We do
          not use OpenAI. We do not use customer questions or property records to train a
          Stevenson Systems model. The last 50 chat messages are stored with the user’s
          TruSpace account so a conversation can be continued, and are held as described in
          Section 9.
        </p>
        <p>
          <span className="font-semibold text-foreground">The basis for sending it.</span>{' '}
          Anthropic receives these records as a contractor under MSA Section 8.3, which
          permits disclosure to contractors who need the information, have agreed in writing
          to keep it confidential, and may use it only to facilitate their work for
          Stevenson Systems. Anthropic is listed with our other providers in Section 6.
        </p>
        <p>
          <span className="font-semibold text-foreground">Your controls.</span> An
          organization administrator may ask us to disable AI-powered features for their
          entire account. Write to <LegalMailto email="support@truspace.com" /> to request
          this.
        </p>
        <p>
          <span className="font-semibold text-foreground">Accuracy.</span> AI-generated
          output can be incomplete or wrong. It is informational and assistive only. Do not
          rely on it for business, safety, or compliance-critical decisions without
          verifying it — and never as a substitute for a Certified Measurement Report.
        </p>
      </LegalSection>

      <LegalSection
        id="privacy-disclose"
        heading="6. HOW WE DISCLOSE PERSONAL INFORMATION"
      >
        <p>
          We disclose personal information only as described here. We do not sell it.
        </p>
        <LegalList>
          <li>
            <span className="font-semibold text-foreground">To your organization.</span>{' '}
            Administrators at the client organization that authorized your account can see
            your account details and your activity in the platform.
          </li>
          <li>
            <span className="font-semibold text-foreground">To service providers.</span>{' '}
            Vendors who host, secure, support, and operate the Services on our behalf. Where
            a provider receives a client organization’s Confidential Information, MSA
            Section 8.3 permits the disclosure only where that provider has agreed in
            writing to keep the information confidential and to use it solely to facilitate
            its work for us, and we engage those providers on that basis. Some entries in
            the table below are not in that category: mapping and address-lookup services
            receive requests directly from your browser rather than from us, and are marked
            accordingly.
          </li>
          <li>
            <span className="font-semibold text-foreground">For legal reasons.</span> Where
            disclosure is required by law or court order. Where the information belongs to a
            client organization, MSA Section 8.4 permits disclosure on that basis and
            requires us to notify the client promptly of the legal request, and we will. We
            do not disclose a client’s Confidential Information to protect our own
            commercial interests.
          </li>
          <li>
            <span className="font-semibold text-foreground">In a business transaction.</span>{' '}
            In connection with a merger, acquisition, or sale of assets, and subject to this
            Notice continuing to apply. Where a client organization’s Confidential
            Information is involved, we will disclose it only as MSA Section 8.3 permits or
            with that organization’s written consent, and MSA Section 19 requires written
            consent before either party assigns the agreement. We do not disclose client
            information to lenders or financing counterparties.
          </li>
        </LegalList>
        <p>
          <span className="font-semibold text-foreground">Service providers.</span> The
          providers below process information on our behalf. Their own terms are available
          on request.
        </p>
        <LegalTable
          label="Service providers that process information on our behalf"
          headers={['Provider', 'What they do', 'Where']}
          rows={SERVICE_PROVIDER_ROWS}
        />
        <p>
          <span className="font-semibold text-foreground">
            Support and feedback automation.
          </span>{' '}
          Support and feedback automation runs on n8n Cloud in Germany. It can read
          support-ticket content and internal Microsoft Teams messages. Where a message
          names a customer contact, that personal information is processed in Germany. This
          is separate from Client Property Data, which is stored in the United States as
          described in Section 8.
        </p>
        <p>
          The current list is published at{' '}
          <LegalExternalLink href="https://www.truspace.com/subprocessors">
            https://www.truspace.com/subprocessors
          </LegalExternalLink>
          . We will give advance notice before adding a provider that handles personal
          information.
        </p>
      </LegalSection>

      <LegalSection
        id="privacy-client-data"
        heading="7. CLIENT PROPERTY DATA AND YOUR ORGANIZATION"
      >
        <p>
          If your personal information appears in TruSpace because a client organization
          uploaded it — for example, as a named tenant contact or occupant in a Space
          Accounting Report — we hold that information on that organization’s behalf.
        </p>
        <p>
          Direct requests to access, correct, or delete that information to the organization
          that supplied it. If you contact us instead, we will refer your request to that
          organization and support them in responding, as our agreements with them require.
          We will not unilaterally alter or delete a client’s records.
        </p>
      </LegalSection>

      <LegalSection id="privacy-where" heading="8. WHERE WE PROCESS INFORMATION">
        <p>
          Client Property Data, and the reports we generate from it, are stored in the
          United States. The application database runs on Supabase and the drawings and
          reports on Cloudflare R2; engineering has confirmed both are configured to United
          States regions, which the providers label North America and Western North America
          respectively. Azure file storage is US West. Application servers run in San
          Francisco. The front end is hosted by Vercel in the United States.
        </p>
        <p>
          Two things are not covered by that sentence. Support tickets and support
          automation may be processed by Zendesk and by n8n Cloud in Germany; a support
          ticket can mention a building, but it is not the property database. And some
          processing happens in transit on a provider’s global network rather than in a
          fixed region — the space-calculation (CAD) service, the TruInsight AI request, and
          map and address lookups. Section 6 identifies each provider and what it handles.
          The statement above describes where Client Property Data is stored at rest.
        </p>
        <p>Section 6 lists each provider and where it processes information.</p>
        <p>
          If you access the Services from outside the United States, your information will
          be transferred to and processed in the United States, which may not provide the
          same level of data protection as your home country.
        </p>
        <p>
          We offer the Services to business customers in the United States and do not target
          or market them to individuals in the European Economic Area or the United Kingdom.
          Section 11 explains the rights available to you if the GDPR or UK GDPR
          nevertheless applies.
        </p>
      </LegalSection>

      <LegalSection id="privacy-retention" heading="9. HOW LONG WE KEEP INFORMATION">
        <p>
          We keep personal information only as long as we need it for the purposes in this
          Notice, or longer where law requires.
        </p>
        <LegalList>
          <li>
            <span className="font-semibold text-foreground">
              Account and Client Property Data.
            </span>{' '}
            Kept for the life of the customer relationship, and as long as we need them to
            provide the service or meet legal requirements.
          </li>
          <li>
            <span className="font-semibold text-foreground">Session tokens.</span> Expire
            after 30 days.
          </li>
          <li>
            <span className="font-semibold text-foreground">
              Session recordings and error reports.
            </span>{' '}
            Deleted after we have used them to fix an issue. We do not keep them as a
            standing archive.
          </li>
          <li>
            <span className="font-semibold text-foreground">Database backups.</span>{' '}
            Supabase takes database backups. We do not use point-in-time recovery. Railway
            does not retain backups or application logs.
          </li>
          <li>
            <span className="font-semibold text-foreground">Support tickets.</span>{' '}
            Zendesk’s standard retention. We have not set a shorter custom period.
          </li>
          <li>
            <span className="font-semibold text-foreground">TruInsight chats.</span> The
            last 50 messages stay with the user account until cleared or the account is
            closed.
          </li>
        </LegalList>
        <p>
          <span className="font-semibold text-foreground">After the contract ends.</span>{' '}
          MSA Section 9.4 gives a client organization a thirty (30) day grace period after
          the termination date to download its Client Property Data, on our receipt of the
          Fees owed for the remainder of the Subscription Term. After that period the MSA
          grants no further right of retrieval and we may remove the data from the TruSpace
          production environment. Access is turned off. Where records remain in an inactive
          state we may still be able to help with a request to{' '}
          <LegalMailto email="support@truspace.com" />, but that is at our discretion and
          should not be relied on — retrieve what you need inside the grace period. We
          delete or anonymize when we no longer have a legitimate business need. Backups may
          hold a copy until the next backup cycle expires.
        </p>
      </LegalSection>

      <LegalSection id="privacy-security" heading="10. HOW WE PROTECT INFORMATION">
        <p>
          We use technical and organizational measures designed to protect personal
          information, including encryption in transit, access controls that limit employee
          access to what their role requires, authentication through Microsoft Entra ID,
          logging, and vendor security review.
        </p>
        <p>
          No method of transmission or storage is completely secure, and we cannot guarantee
          absolute security.
        </p>
        <p>
          <span className="font-semibold text-foreground">Breach notification.</span> If we
          confirm a security incident that affects a client organization’s personal
          information or Client Property Data, we will notify that organization without
          undue delay and will include the information we reasonably have at the time. We
          will update the notice as we learn more.
        </p>
      </LegalSection>

      <LegalSection id="privacy-rights" heading="11. YOUR PRIVACY RIGHTS">
        <LegalSubHeading>California residents</LegalSubHeading>
        <p>
          If you are a California resident, the CCPA as amended by the CPRA gives you the
          following rights.
        </p>
        <LegalList>
          <li>
            <span className="font-semibold text-foreground">Right to know.</span> Request
            the categories and specific pieces of personal information we collected about
            you, the sources, our purposes, and the categories of recipients.
          </li>
          <li>
            <span className="font-semibold text-foreground">Right to delete.</span> Request
            deletion of personal information we collected from you, subject to statutory
            exceptions.
          </li>
          <li>
            <span className="font-semibold text-foreground">Right to correct.</span> Request
            correction of inaccurate personal information.
          </li>
          <li>
            <span className="font-semibold text-foreground">
              Right to opt out of sale or sharing.
            </span>{' '}
            We do not sell personal information and do not share it for cross-context
            behavioral advertising, so there is nothing to opt out of. We will tell you if
            that ever changes.
          </li>
          <li>
            <span className="font-semibold text-foreground">
              Right to limit use of sensitive personal information.
            </span>{' '}
            We use sensitive personal information only for permitted purposes under
            §1798.121(a), so this right does not currently apply.
          </li>
          <li>
            <span className="font-semibold text-foreground">
              Right to non-discrimination.
            </span>{' '}
            We will not deny you service, charge you a different price, or give you a lower
            quality of service for exercising these rights.
          </li>
        </LegalList>
        <p>
          To exercise these rights, email <LegalMailto email="support@truspace.com" /> with
          the subject line “California Privacy Request.” We will verify your identity
          through your account credentials or by matching information you provide against
          our records. You may use an authorized agent; we will ask for proof of their
          authority. We respond within 45 days and may extend once by another 45 days with
          notice.
        </p>
        <p>
          California’s “Shine the Light” law (Cal. Civ. Code §1798.83) lets California
          residents ask about disclosure of personal information to third parties for their
          direct marketing purposes. We do not make such disclosures.
        </p>

        <LegalSubHeading>Residents of other U.S. states</LegalSubHeading>
        <p>
          Residents of states with comprehensive privacy laws — including Virginia,
          Colorado, Connecticut, Texas, Oregon, Montana, and Utah — have rights to access
          and delete their personal information and to obtain a portable copy. Most of those
          states also give a right to correct inaccurate information and a right to appeal a
          refused request; Utah’s law provides neither, though we will handle a Utah
          correction request the same way as any other. Use the same contact address above.
          We do not sell personal information, use it for targeted advertising, or profile
          individuals in ways that produce legal or similarly significant effects.
        </p>

        <LegalSubHeading>EEA and UK residents</LegalSubHeading>
        <p>
          We offer the Services to business customers in the United States and do not target
          or market them to individuals in the European Economic Area or the United Kingdom.
          If the GDPR or UK GDPR nevertheless applies to you, you have rights of access,
          rectification, erasure, restriction, portability, and objection, and the right to
          complain to your supervisory authority. Contact us at the address in Section 15
          and we will handle your request under those standards.
        </p>

        <LegalSubHeading>All users</LegalSubHeading>
        <p>
          You can update your account information in your account settings, or by asking
          your organization’s TruSpace administrator. You can unsubscribe from marketing
          email using the link in any such message; we will still send you service and
          transactional messages.
        </p>
      </LegalSection>

      <LegalSection
        id="privacy-cookies"
        heading="12. COOKIES, SESSION RECORDINGS, AND TRACKING"
      >
        <p>
          We use a small number of first-party cookies and browser-storage entries to keep
          you signed in, remember interface preferences such as light or dark mode, and
          secure the Services. These are strictly necessary to operate the platform. Session
          tokens expire after 30 days.
        </p>

        <LegalSubHeading>Session recordings and error reports</LegalSubHeading>
        <p>
          We use Microsoft Clarity and Sentry only to reproduce bugs and repair TruSpace. We
          do not use recordings to profile users, to market to them, or to gather
          information about how people work. We delete recordings after we have used them
          for that purpose, and we do not keep them as a standing archive.
        </p>
        <p>
          Passwords and codes typed into forms are masked. Recording pauses on sign-in,
          password reset, invitation, and multi-factor screens.
        </p>

        <LegalSubHeading>What we do not use</LegalSubHeading>
        <p>
          We do not use advertising pixels or marketing tags, and we do not run behavioural
          advertising. Product usage analytics are collected by our own servers on our own
          domain. We do not sell personal information.
        </p>
        <p>
          We honor the Global Privacy Control (GPC) and similar browser-based opt-out
          signals where required by law. Most browsers also offer a “Do Not Track” setting;
          there is no common industry standard for responding to it, so we do not respond to
          DNT signals.
        </p>
        <p>
          <span className="font-semibold text-foreground">
            Mapping and address lookup.
          </span>{' '}
          The Portfolio Map is drawn with the Leaflet library using OpenStreetMap data with
          base tiles served by CARTO. Address lookup and certain map features use Google
          Maps. Your browser requests map tiles directly, so those providers receive your IP
          address.
        </p>
      </LegalSection>

      <LegalSection id="privacy-children" heading="13. CHILDREN’S INFORMATION">
        <p>
          TruSpace is a business platform intended solely for use by commercial
          organizations and their personnel. It is not directed to children, and we do not
          knowingly collect personal information from anyone under 18. If you believe a
          child has provided us personal information, write to{' '}
          <LegalMailto email="support@truspace.com" /> and we will delete it.
        </p>
      </LegalSection>

      <LegalSection id="privacy-changes" heading="14. CHANGES TO THIS NOTICE">
        <p>
          We may update this Notice. When we do, we will change the Effective date at the
          top and post the new version in the platform and on our website. If a change
          materially affects how we handle your personal information, we will give notice
          through the Services or by email before it takes effect.
        </p>
        <p>We keep prior versions available so you can see what changed and when.</p>
      </LegalSection>

      <LegalSection id="privacy-contact" heading="15. HOW TO CONTACT US">
        <p>
          Questions, requests, or complaints about this Notice or your personal information:
        </p>
        <p>
          Email: <LegalMailto email="support@truspace.com" />
          <br />
          Post: Stevenson Systems, Inc., 27822 El Lazo Road, Laguna Niguel, CA 92677, United
          States
          <br />
          Phone: 949-297-4200
        </p>
        <p>
          Privacy rights requests (access, correction, deletion):{' '}
          <LegalMailto email="support@truspace.com" />. We will respond within 45 days and
          may extend once by a further 45 days with notice, as Section 11 describes.
        </p>
      </LegalSection>
    </div>
  );
}
